import { useEffect } from "react";
import { motion } from "framer-motion";
import { Target, Sparkles, Search, ListChecks, Bell, Eye, Building2, Newspaper, MousePointerClick, TrendingUp } from "lucide-react";
import { RevShell, ModuleBar, RevHero, SectionHead, RevCTA, BrowserFrame, ComplianceNote } from "./_shared";

const STEPS = [
  { icon: Target, t: "Define your ideal customer", d: "Import your CRM customers and best deals; Scout learns the pattern behind who actually buys." },
  { icon: Search, t: "Enrich & rank", d: "Company and contact records are enriched, then ranked by relevance and likely intent." },
  { icon: ListChecks, t: "Explain every pick", d: "Each prospect comes with a plain-English reason it was selected — no black box." },
  { icon: Bell, t: "You approve first", d: "Nothing activates until you review and approve the list. Scout never contacts anyone on its own." },
];

const SIGNALS = [
  { icon: MousePointerClick, t: "Pricing-page visit", tone: "emerald" },
  { icon: Eye, t: "Repeat visitor", tone: "blue" },
  { icon: MousePointerClick, t: "Abandoned form", tone: "amber" },
  { icon: Bell, t: "Missed phone call", tone: "amber" },
  { icon: Newspaper, t: "Company announcement", tone: "blue" },
  { icon: Building2, t: "New location / job posting", tone: "blue" },
  { icon: TrendingUp, t: "Renewal approaching", tone: "emerald" },
  { icon: Eye, t: "Proposal not opened", tone: "amber" },
];

const TONE = { emerald: "text-emerald-600 bg-emerald-50", blue: "text-brand bg-blue-50", amber: "text-amber-600 bg-amber-50" };

function ProspectMock() {
  const rows = [
    { name: "Harbor Dental Group", score: 92, why: "Visited pricing twice · 40+ staff · new location" },
    { name: "Northside Realty", score: 84, why: "Downloaded guide · repeat visitor this week" },
    { name: "Vista Auto Care", score: 71, why: "Matches ICP · hiring service advisors" },
  ];
  return (
    <BrowserFrame url="app.websitetech.pro/scout/prospects">
      <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-2">Ranked prospects · awaiting your approval</div>
      <div className="space-y-2">
        {rows.map((r) => (
          <div key={r.name} className="flex items-center gap-3 rounded-xl border border-slate-100 bg-slate-50/60 px-3 py-2.5">
            <div className="h-9 w-9 rounded-lg bg-brand text-white grid place-items-center text-xs font-bold shrink-0">{r.score}</div>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-slate-800 truncate">{r.name}</div>
              <div className="text-[11px] text-slate-400 truncate">{r.why}</div>
            </div>
          </div>
        ))}
      </div>
      <div className="flex gap-2 mt-3">
        <div className="flex-1 text-center text-xs font-semibold text-white bg-brand rounded-lg py-2">Approve list</div>
        <div className="text-center text-xs font-semibold text-slate-500 border border-slate-200 rounded-lg py-2 px-3">Edit</div>
      </div>
    </BrowserFrame>
  );
}

export default function Prospecting() {
  useEffect(() => { window.scrollTo(0, 0); document.title = "Scout — Prospecting & Signals | AI Revenue Center"; }, []);
  return (
    <RevShell testid="rev-prospecting-page">
      <RevHero agent="Scout" eyebrow="AI Revenue Center™ · Prospecting & Signals"
        title="Find the right prospects —" accent="and the right moment."
        sub="Scout builds your ideal-customer profile from real deals, enriches records, and watches for genuine buying signals so you reach out when it actually matters."
        visual={<ProspectMock />} />
      <ModuleBar />

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <SectionHead eyebrow="How Scout works" title="From your best customers to your next ones" />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
          {STEPS.map((s, i) => (
            <motion.div key={s.t} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
              className="bg-white border border-slate-200 rounded-2xl card-shadow p-5">
              <div className="h-10 w-10 rounded-xl bg-brand/10 grid place-items-center text-brand"><s.icon size={18} /></div>
              <div className="font-heading font-bold text-slate-900 mt-3">{s.t}</div>
              <p className="text-sm text-slate-500 mt-1.5 leading-relaxed">{s.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="py-16 bg-white border-y border-slate-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHead eyebrow="Revenue Signal Engine" title="Signals that start a conversation" sub="Each approved signal can trigger a controlled workflow — a reply, a task for your team, or a nudge to book." />
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-8">
            {SIGNALS.map((s) => (
              <div key={s.t} className="flex items-center gap-2.5 rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-3">
                <div className={`h-8 w-8 rounded-lg grid place-items-center shrink-0 ${TONE[s.tone]}`}><s.icon size={15} /></div>
                <span className="text-sm text-slate-700 leading-tight">{s.t}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <ComplianceNote>
          We don't claim to own "50+ data sources." Scout works from <strong>your CRM, your customer lists, your own website activity</strong> and lawfully licensed or client-provided data — and never activates outreach without your approval.
        </ComplianceNote>
      </section>

      <RevCTA title="Point Scout at your best customers." sub="Start with a free audit and we'll model your ideal customer from the deals you've already won." />
    </RevShell>
  );
}
